import React, { useState, useMemo, useEffect, Suspense } from "react";
import { List, Empty, Input, Tooltip, Typography, Button, message, Spin } from "antd";
import {
  RightOutlined,
  FileSearchOutlined,
  ShareAltOutlined,
  CarOutlined
} from "@ant-design/icons";
import { getInspectionHistory } from "../../utils/apiService";
import { formatDate } from "../../utils/dateUtils";
import { useNavigate } from "react-router-dom";
import RestrictionModal from "../RestrictionModal";
import { getInspectionRequestDetails } from "../../utils/apiService";
import InspectionRequestModal from "../InspectionRequestModal";

const { Search } = Input;
const { Text } = Typography;

const InspectionHistory = ({ }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [inspectionHistory, setInspectionHistory] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const shareUrlBase = process.env.VITE_SHARE_URL_BASE;
  const [restriction, setRestriction] = useState(false);
  const [requestDetails, setRequestDetails] = useState(false);
  const [selectedId, setselectedId] = useState(false);
  const [isRequest, setIsRequest] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();




  const success = (message) => {
    messageApi.open({
      type: 'success',
      content: message,
    });
  };
  const error = (message) => {
    messageApi.open({
      type: 'error',
      content: message,
    });
  };
  const clearSearch = () => {
    setSearchTerm("");
  };

  const changeSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    loadHistory(0);
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        if (selectedId) {
          const requestDetails = await getInspectionRequestDetails(selectedId)
          setRequestDetails(requestDetails);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchDetails();
  }, [selectedId]);



  const loadHistory = (newPageIndex = 0) => {
    setFetchingHistory(true);

    const size = initialLoad ? 5 : pageSize;

    getInspectionHistory({
      search: searchTerm,
      pageSize: size,
      pageIndex: newPageIndex,
    })
      .then((res) => {

        setFetchingHistory(false);
        const items = Array.isArray(res) ? res : res?.items || [];
        setInspectionHistory(items);

        const total =
          typeof res?.totalCount === "number" ? res.totalCount : items.length;

        setTotalCount(total);
        setPageIndex(newPageIndex);

        if (initialLoad) setInitialLoad(false);
      })
      .catch((err) => {
        setFetchingHistory(false);
        console.error("Failed to load inspection history", err);
      });
  };

  const handleSearch = () => {
    loadHistory(0);
  };

  const handlePageChange = (page) => {
    const newPageIndex = page - 1;
    loadHistory(newPageIndex);
  };

  const handleShare = async (inspectionId, label, text) => {
    const url = `${shareUrlBase}/inspection/${inspectionId}`;
    const isInIframe = window.self !== window.top;

    try {
      if (isInIframe) {
        setRestriction(true);
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: label,
          text: text,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        message.success("Link copied.");
      }
    } catch (err) {
      console.warn("Share cancelled or failed:", err);
      message.error("Share cancelled or failed.");
    }
  };

  const handleBreakdownClick = (breakdownId) => {
    navigate(`/breakdown/${breakdownId}`);
  };

  return (
    <div style={{ padding: "0 16px" }}>
      {contextHolder}
      <Tooltip title="Search inspection history by Registration, Fleet or Company name">
        <Search
          placeholder="Search Inspection History..."
          loading={fetchingHistory}
          onChange={changeSearch}
          style={{ marginBottom: 16 }}
          allowClear
          onClear={clearSearch}
          onSearch={handleSearch}
        />
      </Tooltip>
      <List
        itemLayout="horizontal"
        dataSource={inspectionHistory}
        pagination={
          inspectionHistory && inspectionHistory.length > 0
            ? {
              current: pageIndex + 1,
              pageSize,
              total: totalCount,
              showSizeChanger: false,
              onChange: handlePageChange,
            }
            : false
        }
        renderItem={(item) => (
          <List.Item
            key={item.id}
            style={{ cursor: "pointer" }}
            onClick={() =>
              navigate(`/inspections/responses/${item.id}`, {
                state: { editable: false },
              })
            }
          >
            <List.Item.Meta
              avatar={<FileSearchOutlined style={{ fontSize: 20 }} />}
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{item?.inspectionName}</span>

                </div>
              }
              description={
                <>
                  <Text type="secondary">
                    Registration: {item?.vehicle?.registration || "N/A"}
                  </Text>
                  <br />
                  <Text type="secondary">
                    Fleet: {item?.vehicle?.fleet || "N/A"}
                  </Text>
                  <br />
                  <Text type="secondary">
                    Created on: {formatDate(item.createdDateTime, true)}
                  </Text>
                  <br />
                  <Text type="secondary">Done for: {item?.companyName}</Text>
                  <br />
                  <Text type="secondary">Done by: {item?.createdByName}</Text>

                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    width: 100,
                    gap: 3,
                    marginTop: 10
                  }}>

                    <Button
                      type="primary"
                      icon={<ShareAltOutlined />}
                      onClick={(e) => {
                        e.stopPropagation(); // stops navigating when clicking share
                        handleShare(
                          item.id,
                          `Inspection ${item?.inspectionName}`,
                          `Inspection: ${item?.inspectionName ||
                          item?.inspectionName ||
                          "No inspection name available"
                          }`
                        );
                      }}
                      size="small"
                    >
                      Share
                    </Button>
                    <div style={{ width: '5px' }}></div>
                    {(item.amberBadges > 0 || item.redBadges > 0) && (
                      <Button
                        disabled={fetchingHistory || (item.reference != null ? false : !item.hasNoBreakdownRequest)}
                        loading={fetchingHistory && item.id === selectedId}
                        type="primary"
                        icon={<CarOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();

                          if (item.hasNoBreakdownRequest) {
                            setselectedId(item.id);
                            setIsRequest(true);
                          } else {
                            if (item.breakdownId) {
                              handleBreakdownClick(item.breakdownId)
                            }

                          }
                        }}
                        size="small"
                      >
                        {item.hasNoBreakdownRequest ? 'Event' : (item.reference ?? item.breakdownRef)}
                      </Button>
                    )}

                  </div>

                </>
              }
            />
          </List.Item>
        )}
        locale={{
          emptyText: fetchingHistory ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spin />
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                Loading inspection history...
              </div>
            </div>
          ) : (
            (searchTerm
              ? "No matching inspections found"
              : "No inspection history available")
          ),
        }}
      />
      <RestrictionModal
        open={restriction}
        setOpen={setRestriction}
        item='Share button'
      ></RestrictionModal>
      <InspectionRequestModal
        open={isRequest}
        setOpen={setIsRequest}
        data={requestDetails}
        selectedId={selectedId}
        reload={() => {
          loadHistory(pageIndex);
        }}
        success={() => { success('Event created successfully') }}
        fail={() => { error('Unable to convert inspection into a Event. Please check that the inspection has vehicle data') }}
      >
      </InspectionRequestModal>
    </div>

  );
};

export default InspectionHistory;
